import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowUpRight, ChevronDown, ChevronUp, Info, Sparkles, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { AppLayout } from '../../../components/layouts';
import { CustomModal, StudentCard } from '../../../components/ui';
import HeroSlider, { HeroSlide } from '../../../components/dashboard/HeroSlider';
import StatsCards from '../../../components/dashboard/StatsCards';
import { useRegistration } from '../../../components/layouts/app/RegistrationContext';
import SurveySection from './SurveySection';
import { ReferralSection } from './ReferralSection';
import AdhocRegisterModal, { REOPEN_ADHOC_SESSION_KEY } from './AdhocRegisterModal';
import PersonalClassCard from './PersonalClassCard';
import PersonalClassModal, { type PersonalClassModalHandle } from './PersonalClassModal';
import UserService from '../../../services/user.service';
import { setCart, setCurrency } from '../../../redux/reducers/userSlice';
import { RootState } from '../../../redux/rootReducer';
import { getCardItems } from '../../../utils/childHelpers';
import { toBase64Url } from '../../../utils/Tools';
import {
    modalShell,
} from '../../../components/ui/modalDesign';
import { PARENT_POST_SIGNUP_ENROLLMENT_KEY } from '../../../constants/sessionStorageKeys';

function personalReactivateDraft(item: any) {
    return {
        id: item?.id,
        child: item?.child,
        childId: item?.childId || item?.child?.id,
        grade: item?.grade || '',
        subject: item?.subject || item?.package?.title || '',
        package: item?.package,
        packageId: item?.packageId || item?.package?.id,
        classesPerWeek: Math.min(4, Math.max(1, Number(item?.classesPerWeek) || 2)),
        slots: [],
        prefillSlots: false,
    };
}

function countAttendance(attendance: any): number {
    if (Array.isArray(attendance)) return attendance.length;
    if (typeof attendance === 'number' && !Number.isNaN(attendance)) return attendance;
    if (attendance && typeof attendance === 'object') {
        if (typeof attendance.count === 'number') return attendance.count;
        if (typeof attendance.total === 'number') return attendance.total;
        return Object.keys(attendance).length;
    }
    return 0;
}

function isAiTutorLink(url?: string) {
    if (!url) return false;
    try {
        return new URL(url).hostname.includes('ai.rydlearning.com');
    } catch {
        return /ai\.rydlearning\.com/i.test(url);
    }
}

/** Advert / deep-link for adhoc registration: `...?adhoc=<packageId>` */
function getAdhocPackageIdFromLink(url?: string | null): string | null {
    if (!url) return null;
    try {
        const parsed = new URL(url, window.location.origin);
        return parsed.searchParams.get('adhoc');
    } catch {
        const match = String(url).match(/[?&]adhoc=([^&]+)/i);
        return match?.[1] ? decodeURIComponent(match[1]) : null;
    }
}

export default function Home() {
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const userService = new UserService();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { openAddChildWithWarning } = useRegistration();

    const [survey, setSurvey] = useState<any>([]);
    const [children, setChildren] = useState<any[]>([]);
    const [allChildren, setAllChildren] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAllChildren, setShowAllChildren] = useState(false);
    const [showAiConfirm, setShowAiConfirm] = useState(false);
    const [heroSlides, setHeroSlides] = useState<HeroSlide[]>([]);
    const [pendingExternalLink, setPendingExternalLink] = useState<string | null>(null);
    const [adhocPackage, setAdhocPackage] = useState<any>(null);
    const [showAdhocModal, setShowAdhocModal] = useState(false);
    const [personalClasses, setPersonalClasses] = useState<any[]>([]);
    const [showPersonalModal, setShowPersonalModal] = useState(false);
    const [personalDraft, setPersonalDraft] = useState<any>(null);
    const personalModalRef = useRef<PersonalClassModalHandle>(null);

    const referralId = sessionStorage.getItem('referralId');

    const getSurvey = async () => {
        try {
            const response = await userService.getSurvey();
            if (!response.status) return;
            setSurvey(response.data);
        } catch {
            /* ignore */
        }
    };

    const getCart = async () => {
        try {
            const response = await userService.getCart();
            if (!response.status) return;
            const items = response.data;
            if ((Array.isArray(items) && items.length > 0) || Number(items) > 0) dispatch(setCart(true));
        } catch {
            /* ignore */
        }
    };

    const getCurrency = async () => {
        try {
            const response = await userService.getCurrency();
            if (!response.status) return;
            dispatch(setCurrency(response.data));
        } catch {
            /* ignore */
        }
    };

    const processReferral = async (id: number) => {
        if (!id) return;
        try {
            await userService.updateReferalID(id);
            sessionStorage.removeItem('referralId');
        } catch (error) {
            console.error('Failed to process referral:', error);
        }
    };

    const loadChildren = async () => {
        setLoading(true);
        // Load independently: one failing endpoint must not wipe coding cards from the other.
        const [programs, everyone] = await Promise.all([
            userService.getChildren().catch(() => null),
            userService.getAllChildren().catch(() => null),
        ]);
        if (Array.isArray(programs?.data)) setChildren(programs.data);
        if (Array.isArray(everyone?.data)) setAllChildren(everyone.data);
        setLoading(false);
    };

    const loadAdverts = async () => {
        try {
            const response = await userService.getActiveAdverts();
            if (!response?.status || !Array.isArray(response.data) || response.data.length === 0) {
                setHeroSlides([]);
                return;
            }
            const slides: HeroSlide[] = response.data
                .filter((ad: any) => ad?.title && ad?.buttonLabel && ad?.imageUrl)
                .map((ad: any) => ({
                    id: String(ad.id ?? ad.slug),
                    headline: ad.title,
                    description: ad.description || '',
                    ctaLabel: ad.buttonLabel,
                    bgImage: ad.imageUrl,
                    buttonLink: ad.buttonLink || '',
                }));
            setHeroSlides(slides);
        } catch {
            setHeroSlides([]);
        }
    };

    const loadAdhocPackage = async () => {
        try {
            const response = await userService.getActiveAdhocPackage();
            if (response?.status && response?.data) {
                setAdhocPackage(response.data);
                return response.data;
            }
            setAdhocPackage(null);
            return null;
        } catch {
            setAdhocPackage(null);
            return null;
        }
    };

    const openAdhocRegistration = async (requestedPackageId?: string | null) => {
        const pkg = adhocPackage || (await loadAdhocPackage());
        if (!pkg) {
            toast.warn('No active adhoc package is available right now.');
            return;
        }
        if (requestedPackageId && String(pkg.id) !== String(requestedPackageId)) {
            toast.warn('This adhoc package is no longer active.');
            return;
        }
        setShowAdhocModal(true);
    };

    useEffect(() => {
        if (referralId) processReferral(Number(referralId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [referralId]);

    const loadPersonalClasses = async () => {
        try {
            const response = await userService.getMyPersonalClasses();
            if (response?.status && Array.isArray(response.data)) {
                const ranked = response.data
                    .filter((row: any) => row.paymentStatus !== 'expired')
                    .slice()
                    .sort((a: any, b: any) => {
                        const rank = (row: any) => {
                            if (row.parentCancelled || row.paymentStatus === 'cancelled') return 3;
                            if (row.paymentStatus === 'active' || row.hasAccess) return 0;
                            if (row.paymentStatus === 'paused') return 1;
                            if (row.paymentStatus === 'unpaid' || row.paymentStatus === 'renewal_due' || row.paymentStatus === 'payment_due') return 2;
                            return 4;
                        };
                        const diff = rank(a) - rank(b);
                        if (diff !== 0) return diff;
                        return Number(b.id || 0) - Number(a.id || 0);
                    });
                setPersonalClasses(ranked);
            }
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        getCart();
        getCurrency();
        getSurvey();
        loadChildren();
        loadAdverts();
        loadAdhocPackage();
        loadPersonalClasses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // After signup: open coding Add Program (via RegistrationContext) or Private Lesson modal.
    useEffect(() => {
        try {
            const intent = sessionStorage.getItem(PARENT_POST_SIGNUP_ENROLLMENT_KEY);
            if (!intent) return;
            if (intent === 'coding') {
                sessionStorage.removeItem(PARENT_POST_SIGNUP_ENROLLMENT_KEY);
                return;
            }
            if (intent === 'mathematics' || intent === 'other') {
                sessionStorage.removeItem(PARENT_POST_SIGNUP_ENROLLMENT_KEY);
                setPersonalDraft(
                    intent === 'mathematics'
                        ? { subject: 'Mathematics', prefillSlots: false }
                        : { prefillSlots: false }
                );
                setShowPersonalModal(true);
            }
        } catch {
            /* ignore */
        }
    }, []);

    // Deep-link / advert: /parent/home?adhoc=<packageId>
    useEffect(() => {
        const adhocId = searchParams.get('adhoc');
        if (!adhocId) return;
        void openAdhocRegistration(adhocId);
        const next = new URLSearchParams(searchParams);
        next.delete('adhoc');
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-open adhoc modal after parent adds a child from inside it
    useEffect(() => {
        const onRegEnded = () => {
            try {
                if (sessionStorage.getItem(REOPEN_ADHOC_SESSION_KEY) !== '1') return;
                sessionStorage.removeItem(REOPEN_ADHOC_SESSION_KEY);
            } catch {
                return;
            }
            void loadChildren();
            void openAdhocRegistration();
        };
        window.addEventListener('ryd-reg-flow-ended', onRegEnded);
        return () => window.removeEventListener('ryd-reg-flow-ended', onRegEnded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adhocPackage]);

    // Prefer /program/get/all; if it failed, still build cards from paid programs on /child/get.
    const childrenForCards = useMemo(() => {
        if (Array.isArray(children) && children.length > 0) return children;
        return (allChildren || []).filter((child) =>
            Array.isArray(child?.programs) && child.programs.some((p: any) => p?.isPaid)
        );
    }, [children, allChildren]);
    const cardItems = getCardItems(childrenForCards);
    const visibleCards = showAllChildren ? cardItems : cardItems.slice(0, 2);
    const remainingCount = Math.max(0, cardItems.length - 2);

    const metrics = useMemo(() => {
        let activePrograms = 0;
        let classesAttended = 0;
        let programsCompleted = 0;

        for (const child of children) {
            const programs = Array.isArray(child?.programs) ? child.programs : [];
            for (const program of programs) {
                if (program?.isPaid && !program?.isCompleted) activePrograms += 1;
                if (program?.isPaid && program?.isCompleted) programsCompleted += 1;
                classesAttended += countAttendance(program?.attendance);
            }
        }

        return {
            learners: allChildren.length || children.length,
            activePrograms,
            classesAttended,
            programsCompleted,
        };
    }, [allChildren.length, children]);

    const openAiExplore = () => {
        const parentToken = localStorage.getItem('ryd-parent-token');
        const parentId = userInfo?.id;
        if (!parentToken || !parentId) return;
        const code = toBase64Url({ parentToken, parentId, timestamp: Date.now() });
        window.open(
            `https://ai.rydlearning.com/auth/redirect?code=${encodeURIComponent(code)}`,
            '_blank',
            'noopener,noreferrer'
        );
    };

    const handleSlideCta = (slide: HeroSlide) => {
        const adhocId = getAdhocPackageIdFromLink(slide.buttonLink);
        if (adhocId) {
            void openAdhocRegistration(adhocId);
            return;
        }
        if (isAiTutorLink(slide.buttonLink)) {
            setPendingExternalLink(null);
            setShowAiConfirm(true);
            return;
        }
        if (slide.buttonLink) {
            setPendingExternalLink(slide.buttonLink);
            setShowAiConfirm(true);
        }
    };

    const confirmSlideCta = () => {
        setShowAiConfirm(false);
        if (pendingExternalLink && !isAiTutorLink(pendingExternalLink)) {
            window.open(pendingExternalLink, '_blank', 'noopener,noreferrer');
            setPendingExternalLink(null);
            return;
        }
        setPendingExternalLink(null);
        openAiExplore();
    };

    const slidesWithCta = useMemo(
        () =>
            heroSlides.map((slide) => ({
                ...slide,
                onCta: () => handleSlideCta(slide),
            })),
        // handleSlideCta is stable for slide navigation; depend on slide list + user
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [heroSlides, userInfo?.id]
    );

    const confirmIsAi = !pendingExternalLink || isAiTutorLink(pendingExternalLink);

    return (
        <AppLayout>
            {survey.length > 0 && <SurveySection surveys={survey} />}

            <div className="space-y-6 lg:space-y-8">
                {slidesWithCta.length > 0 && <HeroSlider slides={slidesWithCta} />}

                {showAiConfirm &&
                    createPortal(
                        <div className="fixed inset-0 z-[1020] flex items-end justify-center sm:items-center">
                            <button
                                type="button"
                                aria-label="Dismiss"
                                className="absolute inset-0 bg-[#1B1530]/45 backdrop-blur-[3px]"
                                onClick={() => {
                                    setShowAiConfirm(false);
                                    setPendingExternalLink(null);
                                }}
                            />
                            <div
                                role="alertdialog"
                                aria-labelledby="ai-confirm-title"
                                aria-describedby="ai-confirm-desc"
                                className="relative z-10 w-full max-w-md animate-slideUp overflow-hidden rounded-t-[24px] border border-[#efe6f1] bg-white shadow-[0_-12px_48px_rgba(136,22,122,0.18)] sm:mx-4 sm:rounded-[22px] sm:shadow-[0_20px_60px_rgba(136,22,122,0.16)]"
                            >
                                <div className="h-1.5 w-full bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE]" />
                                <div className="px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-4 sm:px-6 sm:pb-6 sm:pt-5">
                                    <div className="mb-4 flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#7A2F78] to-[#E05AAE] text-white shadow-md">
                                                <Sparkles size={20} />
                                            </div>
                                            <div>
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-ryd-primary">
                                                    Leaving dashboard
                                                </p>
                                                <h2
                                                    id="ai-confirm-title"
                                                    className="font-[AvertaStd-Semibold] text-[18px] leading-tight text-ryd-headerTextPrimary"
                                                >
                                                    {confirmIsAi ? 'Open AI Tutor?' : 'Open link?'}
                                                </h2>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            aria-label="Close"
                                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#94a3b8] transition hover:bg-[#F8F5FA] hover:text-ryd-primary"
                                            onClick={() => {
                                                setShowAiConfirm(false);
                                                setPendingExternalLink(null);
                                            }}
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>

                                    <div
                                        id="ai-confirm-desc"
                                        className="rounded-[14px] border border-[#ebe4f0] bg-[#FBF8FC] px-3.5 py-3"
                                    >
                                        <p className="text-[13px] leading-relaxed text-[#476788]">
                                            {confirmIsAi ? (
                                                <>
                                                    Frank &amp; Franca will open in a{' '}
                                                    <span className="font-semibold text-[#334155]">new tab</span>.
                                                    Your parent dashboard stays open here.
                                                </>
                                            ) : (
                                                <>
                                                    This will open the advert link in a{' '}
                                                    <span className="font-semibold text-[#334155]">new tab</span>.
                                                </>
                                            )}
                                        </p>
                                    </div>

                                    <div className="mt-5 flex items-center gap-2.5">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowAiConfirm(false);
                                                setPendingExternalLink(null);
                                            }}
                                            className="flex-1 rounded-[12px] px-4 py-3 text-[14px] font-semibold text-[#476788] transition hover:bg-[#F8F5FA]"
                                        >
                                            Not now
                                        </button>
                                        <button
                                            type="button"
                                            onClick={confirmSlideCta}
                                            className="inline-flex flex-[1.4] items-center justify-center gap-1.5 rounded-[12px] bg-ryd-primary px-4 py-3 text-[14px] font-semibold text-white shadow-md transition hover:brightness-105"
                                        >
                                            Continue
                                            <ArrowUpRight size={16} strokeWidth={2.4} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}

                <StatsCards
                    learners={metrics.learners}
                    activePrograms={metrics.activePrograms}
                    classesAttended={metrics.classesAttended}
                    programsCompleted={metrics.programsCompleted}
                />

                <section>
                    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <h2 className="font-[AvertaStd-Semibold] text-[22px] text-ryd-headerTextPrimary sm:text-[24px]">
                                My Children
                            </h2>
                            <p className="mt-1 text-[14px] text-[#8b9aab]">
                                A quick look at your learners — open Children for the full table.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={openAddChildWithWarning}
                            className="inline-flex items-center justify-center gap-2 rounded-[14px] bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE] px-5 py-3 text-[14px] font-semibold text-white shadow-md transition hover:scale-[1.02]"
                        >
                            + Add New Child
                        </button>
                    </div>

                    {loading ? (
                        <div className="relative flex h-40 items-center justify-center">
                            <div className="h-[72px] w-[72px] animate-spin rounded-full border border-ryd-primary border-l-white" />
                        </div>
                    ) : cardItems.length > 0 ? (
                        <>
                            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                                {visibleCards.map((item) => (
                                    <StudentCard
                                        key={`${item?.id}-${item?.programs?.[0]?.id ?? 0}`}
                                        item={item}
                                        setTab={(id: number) => navigate(`/parent/activity?child=${id}`)}
                                    />
                                ))}
                            </div>
                            {cardItems.length > 2 && (
                                <div className="mt-6 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowAllChildren((v) => !v)}
                                        className="inline-flex items-center gap-2 rounded-[14px] border border-[#e8dce8] bg-white px-6 py-3 text-[14px] font-semibold text-ryd-primary shadow-sm transition hover:border-ryd-primary hover:shadow-md"
                                    >
                                        {showAllChildren ? (
                                            <>
                                                Show less <ChevronUp size={16} />
                                            </>
                                        ) : (
                                            <>
                                                View {remainingCount} more <ChevronDown size={16} />
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="rounded-[20px] border border-dashed border-[#e8dce8] bg-white px-6 py-16 text-center">
                            <p className="font-[AvertaStd-Semibold] text-[18px] text-ryd-headerTextPrimary">
                                Start with a program
                            </p>
                            <p className="mt-2 text-[14px] text-[#8b9aab]">
                                Add a child to a coding program, or register them for a private lesson.
                            </p>
                            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                                <div className="inline-flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={openAddChildWithWarning}
                                        className="inline-flex rounded-[14px] bg-ryd-primary px-5 py-3 text-[14px] font-semibold text-white"
                                    >
                                        Add Coding Program
                                    </button>
                                    <span className="group relative">
                                        <button type="button" aria-label="About coding programs" className="rounded-full p-1 text-[#8b9aab] hover:text-ryd-primary">
                                            <Info size={16} />
                                        </button>
                                        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-56 -translate-x-1/2 rounded-xl bg-[#1e293b] px-3 py-2 text-left text-[12px] font-normal leading-relaxed text-white group-hover:block group-focus-within:block">
                                            Enroll a child in a group coding program with a cohort, level, and class schedule.
                                        </span>
                                    </span>
                                </div>
                                <div className="inline-flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPersonalDraft(null);
                                            setShowPersonalModal(true);
                                        }}
                                        className="inline-flex rounded-[14px] border border-[#14507a] bg-white px-5 py-3 text-[14px] font-semibold text-[#14507a]"
                                    >
                                        Add Private lesson
                                    </button>
                                    <span className="group relative">
                                        <button type="button" aria-label="About private lessons" className="rounded-full p-1 text-[#8b9aab] hover:text-ryd-primary">
                                            <Info size={16} />
                                        </button>
                                        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-56 -translate-x-1/2 rounded-xl bg-[#1e293b] px-3 py-2 text-left text-[12px] font-normal leading-relaxed text-white group-hover:block group-focus-within:block">
                                            Book one child for a private lesson, choose the subject and weekly times, then check out from the cart.
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                <section className="mt-10">
                    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <h2 className="font-[AvertaStd-Semibold] text-[22px] text-ryd-headerTextPrimary sm:text-[24px]">
                                Private lessons
                            </h2>
                            <p className="mt-1 text-[14px] text-[#8b9aab]">
                                Two current lessons show here. The rest, including active ones, are on the Private lessons page.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setPersonalDraft(null);
                                setShowPersonalModal(true);
                            }}
                            className="inline-flex items-center justify-center gap-2 rounded-[14px] bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE] px-5 py-3 text-[14px] font-semibold text-white shadow-md transition hover:scale-[1.02]"
                        >
                            + Private lesson
                        </button>
                    </div>
                    {personalClasses.length === 0 ? (
                        <div className="rounded-[20px] border border-dashed border-[#d7e7f5] bg-white px-6 py-10 text-center">
                            <p className="text-[14px] text-[#8b9aab]">No current private lessons.</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                                {personalClasses.slice(0, 2).map((item) => (
                                    <PersonalClassCard
                                        key={item.id}
                                        item={item}
                                        onUpdated={() => { void loadPersonalClasses(); }}
                                        onActivate={(lesson) => {
                                            setPersonalDraft(personalReactivateDraft(lesson));
                                            setShowPersonalModal(true);
                                        }}
                                    />
                                ))}
                            </div>
                            {personalClasses.length > 2 ? (
                                <div className="mt-6 flex justify-center">
                                    <Link
                                        to="/parent/private-lessons"
                                        className="inline-flex items-center gap-2 rounded-[14px] border border-[#e8dce8] bg-white px-6 py-3 text-[14px] font-semibold text-ryd-primary shadow-sm transition hover:border-ryd-primary hover:shadow-md"
                                    >
                                        View all private lessons ({personalClasses.length})
                                    </Link>
                                </div>
                            ) : null}
                        </>
                    )}
                </section>

                <div id="invite">
                    <ReferralSection />
                </div>

                <footer className="flex flex-col items-center justify-between gap-3 border-t border-[#efe6f1] pt-6 text-[13px] text-[#8b9aab] sm:flex-row">
                    <p>© {new Date().getFullYear()} RYD Learning. All rights reserved.</p>
                    <div className="flex flex-wrap items-center gap-4">
                        <a href="https://rydlearning.com/terms/#privacy-policy" className="hover:text-ryd-primary" target="_blank" rel="noreferrer">
                            Privacy Policy
                        </a>
                        <a href="https://rydlearning.com/terms/" className="hover:text-ryd-primary" target="_blank" rel="noreferrer">
                            Terms of Service
                        </a>
                        <a href="mailto:learning@rydlearning.com" className="hover:text-ryd-primary">
                            Support
                        </a>
                    </div>
                </footer>
            </div>

            {showPersonalModal ? (
                <CustomModal modalStyle={`${modalShell} max-w-xl`} closeModal={() => personalModalRef.current?.requestClose()}>
                    <PersonalClassModal
                        ref={personalModalRef}
                        key={personalDraft?.id || 'new'}
                        initialEnrollment={personalDraft}
                        onClose={() => setShowPersonalModal(false)}
                        onPaid={() => {
                            void loadPersonalClasses();
                            void loadChildren();
                        }}
                    />
                </CustomModal>
            ) : null}

            {showAdhocModal && adhocPackage && (
                <CustomModal
                    modalStyle={`${modalShell} max-w-xl`}
                    closeModal={() => setShowAdhocModal(false)}
                >
                    <AdhocRegisterModal
                        adhocPackage={adhocPackage}
                        onClose={() => setShowAdhocModal(false)}
                    />
                </CustomModal>
            )}
        </AppLayout>
    );
}
